import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  ngHelper,
  CHAT_MESSAGE_TYPE,
  IChatAttachment,
  IChatDraft,
  IChatMessage,
} from '@cakeiteasy/web-utils';

import { ChatAttachmentService } from './chat-attachment.service';

const STICK_TO_BOTTOM_THRESHOLD_PX = 120;

@Component({
  standalone: false,
  selector: 'ui-chat',
  templateUrl: './ui-chat.component.html',
  styleUrls: ['./ui-chat.component.scss'],
})
export class UiChatComponent implements OnInit, OnChanges {
  @Input() messages: IChatMessage[];
  @Input() imageUploader: (img: string) => Promise<IChatAttachment>;
  @Input() maxImagesCount = 5;
  @Input() placeholder = '';
  /** Changes once per confirmed send; the composer clears when it does, and never otherwise. */
  @Input() draftResetKey: number;
  /** A send is in flight: the composer keeps its content but refuses to send it twice. */
  @Input() sending = false;

  @Output() onMessageSend = new EventEmitter<IChatDraft>();

  @ViewChild('messagesScroll') private _messagesScroll: ElementRef<HTMLElement>;

  public currentMsg: IChatDraft = { text: '', attachments: [] };
  public messageType = CHAT_MESSAGE_TYPE.CUSTOMER;

  public styles: any;
  public chatInputHeight = 60;

  private _chatInputMinHeight = 60;

  constructor(private _attachments: ChatAttachmentService) {}

  ngOnChanges(changes: SimpleChanges) {
    ngHelper.ifChanged(changes, 'messages', () => {
      // Measured before the view updates, so it reflects where the user was reading.
      const shouldStickToBottom = this._isNearBottom();

      if (shouldStickToBottom) {
        this._scrollToBottom();
      }
    });

    ngHelper.ifChanged(changes, 'draftResetKey', (value, previous) => {
      // The first binding is the initial key, not a send: there is nothing to clear yet.
      if (previous !== undefined) {
        this.currentMsg = { text: '', attachments: [] };
      }
    });
  }

  async ngOnInit() {
    this.styles = {
      'font-size.rem': 0.875,
      'box-shadow': 'none',
      'border-width.px': '0',
      'padding-top.px': 5,
      'padding-bottom.px': 5,
      'padding-right.px': 0,
      'padding-left.px': 0,
    };

    this._scrollToBottom();
  }

  public messageInputResize(height: number) {
    const borderWidth = 1;
    this.chatInputHeight = (this._chatInputMinHeight < height ? height : this._chatInputMinHeight) + borderWidth;
  }

  public sendMessage() {
    if (this.sending) {
      return;
    }

    this.onMessageSend.emit({ ...this.currentMsg });
  }

  /** Either the change event of the file input, or a single file handed over by a host. */
  public async onFileInputChanged($event: Event | File) {
    const maxCount = this.maxImagesCount - this.currentMsg.attachments.length;
    let processingFiles: File[] = null;

    if (maxCount <= 0) {
      return;
    }

    if ($event instanceof File) {
      processingFiles = [$event];
    } else {
      const input = $event.target as HTMLInputElement;

      processingFiles = Array.from(input.files).slice(0, maxCount);
      input.value = '';
    }

    // Attached one by one as they finish, so a slow upload does not hold back the ones before it.
    for (const file of processingFiles) {
      const attachment = await this._attachments.createAttachment(file, this.imageUploader);

      if (attachment) {
        this.currentMsg.attachments.push(attachment);
      }
    }
  }

  public deleteAttachment(attachment: IChatAttachment) {
    this.currentMsg.attachments = this.currentMsg.attachments.filter((a) => a.id !== attachment.id);
  }

  public showImage(imgUrl: string) {
    this._attachments.showImage(imgUrl);
  }

  private _isNearBottom(): boolean {
    const el = this._messagesScroll && this._messagesScroll.nativeElement;

    if (!el) {
      return true;
    }

    return el.scrollHeight - el.scrollTop - el.clientHeight < STICK_TO_BOTTOM_THRESHOLD_PX;
  }

  private _scrollToBottom() {
    setTimeout(() => {
      const el = this._messagesScroll && this._messagesScroll.nativeElement;

      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    });
  }

}
